import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import RequestViewModal from "shared/modal/requestViewModal";
import { GetPowerSellerRequestService } from "shared/services/powerSellerRequests";
import { VerifyPowerSellerService } from "shared/services/registrationRequests";
import styles from "./style.module.scss";

interface RegistrationsData {
  id: number;
  name: string;
  title: string;
  email: string;
  phonenumber: string;
  website_url: string;
  address: string;
  address2: string;
  state: string;
  city: string;
  zipcode: string;
  coordinates: {
    type: string;
    coordinates: number[];
  };
  return_refund_policy: string;
  accepted_payment_methods: string;
  current_shipping_methods: string;
  comments: string;
  is_old_User: boolean;
  created_at: Date;
  updated_at: Date;
}

const PowerSellerListings = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    action: "Verify" | "Reject" | null;
    ad: RegistrationsData | null;
  }>({
    open: false,
    action: null,
    ad: null,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [registrationRequests, setRegistrationRequests] = useState<
    RegistrationsData[]
  >([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [viewModal, setViewModal] = useState<{
    open: boolean;
    details: RegistrationsData | null;
  }>({
    open: false,
    details: null,
  });

  const openViewModal = (details: RegistrationsData) => {
    setViewModal({ open: true, details });
  };

  const closeViewModal = () => {
    setViewModal({ open: false, details: null });
  };

  const openConfirmModal = (
    action: "Verify" | "Reject",
    ad: RegistrationsData
  ) => {
    setConfirmModal({ open: true, action, ad });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, action: null, ad: null });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Name", key: "name" },
    { title: "Business Name", key: "title" },
    { title: "Email", key: "email" },
    { title: "Date", key: "date" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllRequests = async (page: number) => {
    try {
      setLoading(true);
      const response = await GetPowerSellerRequestService(page, 10);
      setRegistrationRequests(response.data.data.data || []);
      setTotalItems(response.data.data.totalItems || 0);
    } catch (error) {
      console.error("Error fetching registrationRequests:", error);
      toastMessage("error", "Failed to fetch registrationRequests.");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!confirmModal.ad || !confirmModal.action) return;

    const { id } = confirmModal.ad;

    const payload = {
      is_verified_for_powerselling: confirmModal.action === "Verify",
    };

    try {
      setActionLoading(id);
      await VerifyPowerSellerService(id, payload);
      toastMessage("success", `${confirmModal.action} action successful.`);
      closeConfirmModal();
      handleGetAllRequests(currentPage);
    } catch (error) {
      console.error(`Error performing ${confirmModal.action}:`, error);
      toastMessage("error", `Failed to ${confirmModal.action.toLowerCase()}.`);
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    handleGetAllRequests(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Registration Requests List"
          isEmpty={!loading && !registrationRequests.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : registrationRequests.map((data) => (
                <tr key={data.id}>
                  <td>{data.name}</td>
                  <td>{data.title}</td>
                  <td>{data.email}</td>
                  <td>{moment(data.created_at).format("MMM DD, YYYY")}</td>
                  <td>
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <button onClick={() => openViewModal(data)}>
                          <Icons.Eye />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Verify">
                        <button
                          onClick={() => openConfirmModal("Verify", data)}
                        >
                          <Icons.Check />
                        </button>
                      </CustomToolTip>
                      <CustomToolTip label="Reject">
                        <button
                          onClick={() => openConfirmModal("Reject", data)}
                        >
                          <Icons.Reject />
                        </button>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />
      {confirmModal.open && (
        <ConfirmationModal
          show={confirmModal.open}
          title={`Confirm ${confirmModal.action}`}
          actionButtonText={confirmModal.action}
          subtitle={`Are you sure you want to ${confirmModal.action?.toLowerCase()} this request?`}
          loading={actionLoading === confirmModal.ad?.id}
          handleClose={closeConfirmModal}
          handleSubmit={handleAction}
        />
      )}

      {viewModal.open && viewModal.details && (
        <RequestViewModal
          title="PowerSeller Request Details"
          show={viewModal.open}
          handleClose={closeViewModal}
          details={viewModal.details}
        />
      )}
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 5 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default PowerSellerListings;
